import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MEDIA, TYPE_LABELS } from "@/mocks/media";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/base/MediaCard";
import ShareDialog from "@/components/feature/ShareDialog";
import EmptyState from "@/components/base/EmptyState";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = MEDIA.find((m) => m.id === id);
  const { isFavorite, toggle } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);

  usePageMeta({
    title: item ? `${item.title}（${item.year}） - WWW.17C详情` : "影视详情 - WWW.17C",
    description: item
      ? `${item.summary} ${item.title}由${item.director}执导，${item.cast.slice(0, 3).join("、")}主演。`
      : "WWW.17C 影视作品详情页。",
    keywords: item ? `${item.title},${TYPE_LABELS[item.type]},${item.genres.join(",")},WWW.17C` : "影视详情,WWW.17C",
  });

  const related = useMemo(() => {
    if (!item) return [];
    return MEDIA.filter((m) => m.id !== item.id)
      .map((m) => {
        const overlap = m.tags.filter((t) => item.tags.includes(t)).length;
        const typeScore = m.type === item.type ? 2 : 0;
        const genreScore = m.genres.filter((g) => item.genres.includes(g)).length;
        return { m, score: typeScore + genreScore + overlap };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((r) => r.m);
  }, [item]);

  if (!item) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-8">
        <EmptyState
          icon="ri-movie-2-line"
          title="该影视内容不存在或已被删除"
          description="你访问的作品可能已下架，或是链接有误。不妨回到首页看看其他精彩内容。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/");
          }}
        />
      </div>
    );
  }

  const favorited = isFavorite(item.id);

  const metaRows: { label: string; value: string }[] = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join(" / ") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "上映时间", value: `${item.year} 年` },
    { label: "片长", value: item.duration },
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: TYPE_LABELS[item.type], to: `/category/${item.type}` }, { label: item.title }]}
      />

      {/* 详情主区 */}
      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <div className="mx-auto w-full max-w-[320px] lg:mx-0">
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 海报`}
              title={`${item.title} - WWW.17C`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
              {TYPE_LABELS[item.type]}
            </span>
            {item.genres.map((g) => (
              <span key={g} className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                {g}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            {item.subtitle} · {item.year} · {item.region}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1.5 text-2xl font-bold text-accent-600">
              <i className="ri-star-fill text-xl"></i>
              {item.rating.toFixed(1)}
            </span>
            <span className="h-5 w-px bg-background-300"></span>
            <span className="flex items-center gap-1.5 text-sm text-foreground-500">
              <i className="ri-fire-line text-accent-500"></i>
              {item.views.toLocaleString()} 次浏览
            </span>
            <span className="flex items-center gap-1.5 text-sm text-foreground-500">
              <i className="ri-calendar-check-line"></i>
              更新于 {item.updatedAt}
            </span>
          </div>

          {/* 操作按钮 */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => toggle(item.id)}
              className={`flex items-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                favorited
                  ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                  : "bg-primary-600 text-background-50 hover:bg-primary-700"
              }`}
            >
              <i className={favorited ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex items-center gap-1.5 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-secondary-400 hover:text-secondary-600"
            >
              <i className="ri-share-line"></i>
              分享
            </button>
          </div>
          <p className="mt-2 text-xs text-foreground-400">
            * 收藏保存在本地浏览器，分享与收藏均为前端演示功能。
          </p>

          {/* 元信息 */}
          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-2">
            {metaRows.map((row) => (
              <div key={row.label} className="flex gap-3 text-sm">
                <span className="w-16 shrink-0 text-foreground-400">{row.label}</span>
                <span className="min-w-0 flex-1 text-foreground-800">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 简介 / 剧情梗概 */}
      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <section className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
            作品简介
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700">{item.summary}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-50 px-3 py-1 text-xs text-secondary-700">
                {tag}
              </span>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
            剧情梗概
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700">{item.plot}</p>
          <Link
            to={`/category/${item.type}`}
            className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-secondary-600 transition-colors hover:text-primary-600"
          >
            查看更多{TYPE_LABELS[item.type]}
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </section>
      </div>

      {/* 相关推荐 */}
      <section className="mt-12">
        <h2 className="mb-6 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-6 w-1.5 rounded-full bg-accent-500"></span>
          相关推荐
        </h2>
        {related.length === 0 ? (
          <EmptyState title="暂无相关推荐" description="换个作品看看吧。" />
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {related.map((r) => (
              <MediaCard key={r.id} item={r} />
            ))}
          </div>
        )}
      </section>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} />
    </div>
  );
}